import React from "react";
import {
  FormField,
  TextInput,
  FormGroupCard,
  SelectField,
  RichTextArea,
  ImageInput,
  MasterSkuInputGroup,
} from "src/shared/forms/components";
import type { WizardStepComponentProps } from "src/shared/forms/types";
import type {
  ProductWizardData,
  ImageItem,
} from "../../../hooks/useProductWizard";
import type { MerchantProduct } from "src/shared/components/MerchantProductModal/types";

interface StoreOption {
  value: string;
  label: string;
  marketplace: string;
}

interface BasicInfoStepProps extends WizardStepComponentProps {
  data: ProductWizardData["basicInfo"];
  storeOptions: StoreOption[];
  accountsLoading: boolean;
  accountsError: string | null;
  onLinkProduct: () => void;
  onUnlinkProduct: () => void;
}

const BasicInfoStep: React.FC<BasicInfoStepProps> = ({
  data,
  onDataChange,
  storeOptions,
  accountsLoading,
  accountsError,
  onLinkProduct,
  onUnlinkProduct,
  errors = [],
}) => {
  const handleLinkProduct = () => {
    onLinkProduct();
  };

  const handleUnlinkProduct = () => {
    onUnlinkProduct();
    onDataChange({
      ...data,
      linkedMerchantProduct: null,
      masterSku: "",
    });
  };

  return (
    <div className="tw-space-y-6" data-testid="wizard-step-basicInfo">
      {/* Master SKU Group */}
      <FormGroupCard
        title="Master SKU"
        subtitle="Generated automatically based on merchant product selection."
        data-testid="wizard-basicInfo-masterSku-group"
      >
        <FormField name="masterSku" label="Master SKU" required>
          <MasterSkuInputGroup<MerchantProduct>
            value={data.masterSku || ""}
            linkedProduct={data.linkedMerchantProduct}
            onLinkProduct={handleLinkProduct}
            onUnlinkProduct={handleUnlinkProduct}
          />
        </FormField>
      </FormGroupCard>

      {/* Store Selection Group */}
      <FormGroupCard
        title="Store Selection"
        subtitle="Choose the store where you want to list this product."
        data-testid="wizard-basicInfo-storeSelection-group"
      >
        <FormField
          name="storeSelection"
          label="Select Store"
          required
          error={errors.find((e) => e.includes("store"))}
        >
          <SelectField
            value={data.selectedStore || ""}
            onChange={(value) =>
              onDataChange({ ...data, selectedStore: value })
            }
            placeholder={accountsLoading ? "Loading stores..." : "Select a store"}
            disabled={accountsLoading}
            options={[
              {
                value: "",
                label: accountsLoading ? "Loading stores..." : "Select a store",
                disabled: true,
              },
              ...storeOptions,
            ]}
            className="tw-w-full"
          />
          {accountsError && (
            <div className="tw-mt-2 tw-text-sm tw-text-red-600">
              Error loading stores: {accountsError}
            </div>
          )}
        </FormField>
      </FormGroupCard>

      {/* Product Details Group */}
      <FormGroupCard
        title="Product Details"
        subtitle="Provide basic information about your product."
        data-testid="wizard-basicInfo-productDetails-group"
      >
        <FormField
          name="productName"
          label="Product Name"
          required
          error={errors.find((e) => e.includes("name"))}
        >
          <TextInput
            value={data.name || ""}
            onChange={(value) => onDataChange({ ...data, name: value })}
            placeholder="Enter product name..."
            className="tw-w-full"
          />
        </FormField>

        <FormField
          name="productDescription"
          label="Product Description"
          required
          error={errors.find((e) => e.includes("description"))}
        >
          <RichTextArea
            value={data.description || ""}
            onChange={(value) => onDataChange({ ...data, description: value })}
            placeholder="Describe your product in detail..."
            maxLength={2000}
            showCharacterCount={true}
            showToolbar={true}
            minHeight="140px"
            className="tw-w-full"
          />
        </FormField>
      </FormGroupCard>

      {/* Product Images Group */}
      <FormGroupCard
        title="Product Images"
        subtitle="Upload high-quality images to showcase your product."
        data-testid="wizard-basicInfo-images-group"
      >
        <FormField
          name="productImages"
          label=""
          required
          error={errors.find((e) => e.includes("image"))}
        >
          <ImageInput
            images={data.images || []}
            onChange={(images: ImageItem[]) =>
              onDataChange({ ...data, images })
            }
            maxImages={9}
            className="tw-w-full"
          />
        </FormField>

        {/* Product Video URL */}
        <div className="tw-flex tw-flex-col tw-gap-2 tw-pt-4 tw-border-t tw-border-gray-200">
          <FormField
            name="productVideoUrl"
            label="Product Video URL"
            helpText="Add a video URL (YouTube, Vimeo, etc.) to showcase your product in action."
          >
            <TextInput
              value={data.videoUrl || ""}
              onChange={(value) => onDataChange({ ...data, videoUrl: value })}
              placeholder="e.g., https://youtube.com/watch?v=..."
              type="url"
              className="tw-w-full"
            />
          </FormField>
        </div>
      </FormGroupCard>
    </div>
  );
};

export default BasicInfoStep;
